import React, { FC } from 'react'
import { Link } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import './user-resource-status-cell.css'

// COMPONENTS
import * as Icons from '@icons'

// TYPES
import type { UserResource, EmailAddressResource, PhoneNumberResource, Web3WalletResource } from '@clerk/types'

// UTILITIES
import { flatten } from '@utils/string-helper'
import { isObject } from '@utils/type-checkers'
import { formatInput } from '@utils/phone-number-helper'


interface Props extends Omit<React.HTMLAttributes<HTMLAnchorElement>, 'resource'> {
    resource: EmailAddressResource | PhoneNumberResource | Web3WalletResource
}

interface ResourceStatus {
    id: string
    primary: boolean
    verified: boolean
    type?: 'email' | 'phone' | 'web3Wallet'
    label?: string
}

const UserResourceStatusCell: FC<Props> = (props) => {
    const { className: classProps, resource, ...aProps } = props
    const { user } = useUser()

    const { type, id, label, primary, verified } = parseUserResource(user, resource)
    const className = flatten("user-resource-status-cell", { primary, verified }, "flex flex-row items-center", classProps)

    // ToDo: Type Safe `to` Link
    return (
        <Link to={`./${type}/${id}${!verified ? '/verify' : ''}`} className={className} data-resource-type={type} {...aProps}>
            <p className="resource-label flex flex-col items-stretch">
                <span>{label}</span>
                <span className="text-body-2 resource-status">
                    {verified ? 'Verified' : 'Unverified'}
                    {primary ? ' - Primary' : ''}
                </span>
            </p>
            {(type !== 'phone' && type !== 'email') && <Icons.Chevron width="1.25rem" height="1.25rem" facing="right" />}
        </Link>
    )
}

const parseUserResource = (user?: UserResource | null, resource?: EmailAddressResource | PhoneNumberResource | Web3WalletResource): ResourceStatus => {
    const base = {
        id: resource?.id || '',
        primary: resource ? user?.isPrimaryIdentification(resource) ?? false : false,
        verified: resource?.verification.status === 'verified' // ToDo: Handle additional verification statuses
    }
    if (isEmailResource(resource)) return ({ ...base, type: 'email', label: resource.emailAddress })
    if (isPhoneResource(resource)) return ({ ...base, type: 'phone', label: `+1 ${formatInput(resource.phoneNumber)}` })
    if (isWeb3Resource(resource)) return ({ ...base, type: 'web3Wallet', label: resource.web3Wallet })
    return base
}
const isEmailResource = (value: unknown): value is EmailAddressResource => (isObject(value) && 'emailAddress' in value)
const isPhoneResource = (value: unknown): value is PhoneNumberResource => (isObject(value) && 'phoneNumber' in value)
const isWeb3Resource = (value: unknown): value is Web3WalletResource => (isObject(value) && 'web3Wallet' in value)

export default UserResourceStatusCell